import {
  RegionalHighlightCustomerRelation,
  RegionalHighlightListingMatchAction,
  RegionalHighlightListingMatchReason,
  RegionalHighlightSlotDisabledReason,
  RegionalHighlightSlotState,
  type RegionalHighlightActiveBooking,
  type RegionalHighlightResolvedListing,
  type RegionalHighlightSlot,
  type RegionalHighlightSlotsResponse,
} from '@/types/regionalHighlight';

type RecordValue = Record<string, unknown>;

const asRecord = (value: unknown): RecordValue | undefined =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as RecordValue)
    : undefined;

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const firstString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    const stringValue = asString(value);
    if (stringValue) return stringValue;
  }
  return undefined;
};

const normalizeDistrictResolutionStatus = (
  value: unknown,
): RegionalHighlightSlot['districtResolutionStatus'] =>
  value === 'RESOLVED' || value === 'MISSING_LABEL' || value === 'UNKNOWN_LABEL'
    ? value
    : 'UNKNOWN_LABEL';

const normalizeSlotState = (value: unknown): RegionalHighlightSlotState =>
  Object.values(RegionalHighlightSlotState).includes(value as RegionalHighlightSlotState)
    ? (value as RegionalHighlightSlotState)
    : RegionalHighlightSlotState.AVAILABLE;

const normalizeDisabledReason = (
  value: unknown,
): RegionalHighlightSlot['controls']['disabledReason'] =>
  Object.values(RegionalHighlightSlotDisabledReason).includes(
    value as RegionalHighlightSlotDisabledReason,
  )
    ? (value as RegionalHighlightSlotDisabledReason)
    : undefined;

const normalizeListingMatch = (value: unknown): RegionalHighlightSlot['listingMatch'] => {
  const match = asRecord(value);
  const reason = match?.reason;
  const action = match?.action;
  if (
    !match ||
    typeof match.assignable !== 'boolean' ||
    !Object.values(RegionalHighlightListingMatchReason).includes(
      reason as RegionalHighlightListingMatchReason,
    )
  ) {
    return undefined;
  }

  return {
    assignable: match.assignable,
    reason: reason as RegionalHighlightListingMatchReason,
    ...(Object.values(RegionalHighlightListingMatchAction).includes(
      action as RegionalHighlightListingMatchAction,
    )
      ? { action: action as RegionalHighlightListingMatchAction }
      : {}),
  };
};

const normalizeDistrictNames = (value: unknown): Record<string, string> | undefined => {
  const names = asRecord(value);
  if (!names) return undefined;

  return Object.entries(names).reduce<Record<string, string>>((result, [locale, label]) => {
    if (typeof label === 'string') result[locale] = label;
    return result;
  }, {});
};

const normalizeIneligibleReason = (
  value: unknown,
): RegionalHighlightResolvedListing['ineligibleReason'] =>
  value === 'LISTING_NOT_FOUND' ||
  value === 'LISTING_NOT_FOR_SALE' ||
  value === 'LISTING_DISTRICT_UNRESOLVED'
    ? value
    : undefined;

const normalizeRelation = (value: unknown): RegionalHighlightCustomerRelation | undefined => {
  switch (value) {
    case 'DIRECT':
    case RegionalHighlightCustomerRelation.DIRECT_CUSTOMER:
      return RegionalHighlightCustomerRelation.DIRECT_CUSTOMER;
    case 'HEAD':
    case RegionalHighlightCustomerRelation.HEAD_SUB_CUSTOMER:
      return RegionalHighlightCustomerRelation.HEAD_SUB_CUSTOMER;
    case 'SIBLING':
    case RegionalHighlightCustomerRelation.SAME_HEAD_SIBLING:
      return RegionalHighlightCustomerRelation.SAME_HEAD_SIBLING;
    case RegionalHighlightCustomerRelation.UNRELATED_CUSTOMER:
      return RegionalHighlightCustomerRelation.UNRELATED_CUSTOMER;
    default:
      return undefined;
  }
};

const districtLabel = (value: unknown, fallback?: string): string | undefined => {
  const names = asRecord(value);
  return firstString(names?.en, names?.de, names?.fr, names?.it, fallback);
};

const normalizeBooking = (value: unknown): RegionalHighlightActiveBooking | undefined => {
  const booking = asRecord(value);
  const bookingId = asString(booking?.bookingId);
  const listingId = asString(booking?.listingId);
  const begin = asString(booking?.begin);
  const bookingCustomerId = firstString(booking?.bookingCustomerId, booking?.customerId);

  if (!bookingId || !listingId || !begin || !bookingCustomerId) return undefined;

  return {
    bookingId,
    bookingCustomerId,
    listingId,
    begin,
    customerRelation: normalizeRelation(firstString(booking?.customerRelation, booking?.ownership)),
  };
};

const normalizeControls = (raw: RecordValue): RegionalHighlightSlot['controls'] => {
  const controls = asRecord(raw.controls);
  return {
    canAssign: (controls?.canAssign ?? raw.canAssign) === true,
    canReplace: (controls?.canReplace ?? raw.canReplace) === true,
    canDeactivate: (controls?.canDeactivate ?? raw.canDeactivate) === true,
    disabledReason: normalizeDisabledReason(controls?.disabledReason),
  };
};

const normalizeSlot = (value: unknown): RegionalHighlightSlot | undefined => {
  const raw = asRecord(value);
  if (!raw) return undefined;

  const assignment = asRecord(raw.assignment);
  const district = firstString(raw.districtId, raw.district);
  const slot: RegionalHighlightSlot = {
    slotId: firstString(raw.slotId) ?? '',
    slotOwnerCustomerId: firstString(raw.slotOwnerCustomerId, raw.ownerCustomerId) ?? '',
    productId: firstString(raw.productId) ?? '',
    productCode: firstString(raw.productCode, raw.productId) ?? '',
    productName: firstString(raw.productName) ?? 'Regional Highlight',
    districtId: district,
    districtLabel: firstString(raw.districtLabel) ?? districtLabel(raw.districtName, district),
    districtResolutionStatus: normalizeDistrictResolutionStatus(raw.districtResolutionStatus),
    contractStartDate: firstString(raw.contractStartDate, raw.validFrom, assignment?.begin) ?? '',
    contractEndDate: firstString(raw.contractEndDate, raw.validUntil) ?? null,
    status: normalizeSlotState(firstString(raw.status, raw.state)),
    controls: normalizeControls(raw),
    activeBooking: normalizeBooking(raw.activeBooking ?? raw.assignment),
    listingMatch: normalizeListingMatch(raw.listingMatch),
  };

  return slot.slotId && slot.productId && slot.slotOwnerCustomerId ? slot : undefined;
};

export const normalizeRegionalHighlightSlotsResponse = (
  value: unknown,
): RegionalHighlightSlotsResponse => {
  const raw = asRecord(value);
  const rawSlots = raw?.slots;
  const firstSlot = Array.isArray(rawSlots) ? asRecord(rawSlots[0]) : undefined;

  // Keep existing normalized fixtures and responses byte-for-byte compatible.
  if (firstSlot?.slotOwnerCustomerId && firstSlot.controls) {
    return value as RegionalHighlightSlotsResponse;
  }

  const listing = asRecord(raw?.listing);
  return {
    slots: Array.isArray(rawSlots)
      ? rawSlots.map(normalizeSlot).filter((slot): slot is RegionalHighlightSlot => Boolean(slot))
      : [],
    listing: listing
      ? {
          listingId: asString(listing.listingId) ?? '',
          resolved: listing.resolved === true,
          offerType:
            listing.offerType === 'RENT' ? 'RENT' : listing.offerType === 'BUY' ? 'BUY' : undefined,
          districtId: asString(listing.districtId),
          districtName: normalizeDistrictNames(listing.districtName),
          eligible: listing.eligible === true,
          ineligibleReason: normalizeIneligibleReason(listing.ineligibleReason),
        }
      : undefined,
  };
};
